import { useState, useCallback } from 'react'
import {
  FileInfo,
  listFiles,
  uploadFile,
  downloadFile,
  deleteFile,
  createShareLink,
} from '../services/api'

export interface ShareResult {
  shareId: string
  shareUrl: string
  password: string
}

interface UseFilesReturn {
  files: FileInfo[]
  isLoading: boolean
  error: string | null
  uploadProgress: number | null
  refreshFiles: () => Promise<void>
  upload: (file: File) => Promise<void>
  download: (key: string) => Promise<void>
  remove: (key: string) => Promise<void>
  share: (key: string, expiresIn: number, password: string) => Promise<ShareResult>
}

export function useFiles(): UseFilesReturn {
  const [files, setFiles] = useState<FileInfo[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)

  const refreshFiles = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const fileList = await listFiles()
      setFiles(fileList)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load files')
    } finally {
      setIsLoading(false)
    }
  }, [])

  const upload = useCallback(async (file: File) => {
    setError(null)
    setUploadProgress(0)
    try {
      await uploadFile(file, (progress) => {
        setUploadProgress(progress)
      })
      setUploadProgress(null)
      await refreshFiles()
    } catch (err) {
      setUploadProgress(null)
      setError(err instanceof Error ? err.message : 'Failed to upload file')
      throw err
    }
  }, [refreshFiles])

  const download = useCallback(async (key: string) => {
    setError(null)
    try {
      await downloadFile(key)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download file')
      throw err
    }
  }, [])

  const remove = useCallback(async (key: string) => {
    setError(null)
    try {
      await deleteFile(key)
      await refreshFiles()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete file')
      throw err
    }
  }, [refreshFiles])

  const share = useCallback(async (key: string, expiresIn: number, password: string): Promise<ShareResult> => {
    setError(null)
    try {
      const response = await createShareLink(key, expiresIn, password)
      // Construct the full share URL
      const shareUrl = `${window.location.origin}/shared/${response.shareId}`
      return {
        shareId: response.shareId,
        shareUrl,
        password,
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create share link')
      throw err
    }
  }, [])

  return {
    files,
    isLoading,
    error,
    uploadProgress,
    refreshFiles,
    upload,
    download,
    remove,
    share,
  }
}
